"use client";

import { useTranslations } from "next-intl";
import * as React from "react";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "@/components/ui/skeleton";
import type { ExtendedUserStake } from "@/hooks/stake/useFetchStakeContractData";
import { StakeCard } from "./StakeCard";

interface StakeCardListProps {
  stakes: ExtendedUserStake[];
  isLoading?: boolean;
}

const INITIAL_CHUNK = 10;
const SKELETON_COUNT = 3;

export const StakeCardList: React.FC<StakeCardListProps> = ({
  stakes,
  isLoading = false,
}) => {
  const t = useTranslations("stake");
  const [visibleCount, setVisibleCount] = React.useState(INITIAL_CHUNK);
  const { ref, inView } = useInView({ threshold: 0 });

  React.useEffect(() => {
    if (inView && visibleCount < stakes.length) {
      setVisibleCount((c) => Math.min(c + 10, stakes.length));
    }
  }, [inView, stakes.length, visibleCount]);

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="stake-card-list-loading">
        {Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
          <Skeleton
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list, no reorder
            key={idx}
            variant="muted"
            className="h-20 w-full rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (stakes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-sm text-text-secondary text-center">
          {t("card.emptyList")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {stakes.slice(0, visibleCount).map((stake) => (
        <StakeCard key={stake.stake_id} stake={stake} />
      ))}
      <div ref={ref} className="h-2" />
    </div>
  );
};
